/** @fileoverview Swaps a one-time sign-in code for a hosted app session straight from the apps domain. */

import { getCurrentGatewayUrl } from './hostConfig';

export type AppSessionResult =
  | { kind: 'ok'; appName: string; token: string; expiresIn: number }
  | { kind: 'notFound' }
  | { kind: 'previewForbidden' }
  | { kind: 'expired' }
  | { kind: 'error'; message: string };

export type AppSessionRefreshResult =
  | { kind: 'ok'; token: string; expiresIn: number }
  | { kind: 'expired' }
  | { kind: 'error'; message: string };

type Props = {
  address: string;
  code: string;
};

const GENERIC_ERROR = 'Unable to open this app. Please try again.';

/** Sends the code to the gateway without cookies or tokens and maps the answer to a small result. */
export const requestAppSession = async ({ address, code }: Props): Promise<AppSessionResult> => {
  const gatewayUrl = getCurrentGatewayUrl().replace(/\/$/, '');
  if (!gatewayUrl) {
    return { kind: 'error', message: GENERIC_ERROR };
  }
  try {
    const response = await fetch(
      `${gatewayUrl}/api/fabrik-apps/${encodeURIComponent(address)}/session`,
      {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      }
    );
    if (response.status === 404) {
      return { kind: 'notFound' };
    }
    if (response.status === 403) {
      return { kind: 'previewForbidden' };
    }
    if (response.status === 401) {
      return { kind: 'expired' };
    }
    if (!response.ok) {
      return { kind: 'error', message: GENERIC_ERROR };
    }
    const body = (await response.json()) as {
      token?: string;
      expiresIn?: number;
      app?: { name?: string };
    };
    if (!body.token) {
      return { kind: 'error', message: GENERIC_ERROR };
    }
    return {
      kind: 'ok',
      appName: body.app?.name ?? '',
      token: body.token,
      expiresIn: body.expiresIn ?? 3600,
    };
  } catch {
    return { kind: 'error', message: GENERIC_ERROR };
  }
};

/** Renews a still-valid hosted app pass before it expires. */
export const refreshAppSession = async ({
  address,
  token,
}: {
  address: string;
  token: string;
}): Promise<AppSessionRefreshResult> => {
  const gatewayUrl = getCurrentGatewayUrl().replace(/\/$/, '');
  if (!gatewayUrl) {
    return { kind: 'error', message: 'Unable to continue this session. Please try again.' };
  }
  try {
    const response = await fetch(
      `${gatewayUrl}/api/fabrik-apps/${encodeURIComponent(address)}/session/refresh`,
      {
        method: 'POST',
        credentials: 'omit',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if ([401, 403, 404].includes(response.status)) {
      return { kind: 'expired' };
    }
    if (!response.ok) {
      return { kind: 'error', message: 'Unable to continue this session. Please try again.' };
    }
    const body = (await response.json()) as { token?: string; expiresIn?: number };
    return body.token
      ? { kind: 'ok', token: body.token, expiresIn: body.expiresIn ?? 3600 }
      : { kind: 'error', message: 'Unable to continue this session. Please try again.' };
  } catch {
    return { kind: 'error', message: 'Unable to continue this session. Please try again.' };
  }
};
